/* eslint-disable quotes */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import '../static/icons-extended.css';
import '../static/icons.css';
import './styles/main.less';
import 'idempotent-babel-polyfill';

import React from 'react';
import ReactDOM from 'react-dom';
import { addLocaleData } from 'react-intl';
import { Provider } from 'react-redux';
import { trackEvent } from './shared/tracking/BookingFlowTracker';
import initTrackingModules from './shared/tracking/helpers';
import * as Translations from './state/actions/translations';
import configureStore from './state/configureStore';
import * as Search from 'Actions/search';
import * as Settings from 'Actions/settings';
import * as WidgetPool from 'Actions/widgetPool';
import EnquiryFormContainer from 'Components/EnquiryForm/EnquiryFormContainer';
import List from 'Components/List';
import PropertyListContainer from 'Components/PropertyList/containers/PropertyListContainer';
import RewardProgram from 'Components/RewardProgram';
import SearchBar from 'Components/SearchBar';
import { DEFAULT_FALLBACK_LANGUAGE } from 'Constants/App';
import { SETTINGS_NEEDED_PARAMS } from 'Constants/WidgetConfig';
import { appendStylesLinkToHead, appendStylesToHead } from 'Helpers';
import { locales } from 'Services';
import { UrlTransformers } from 'Shared/urltransformer/UrlTransformers';
import ConfigurationAdapter from 'Shared/utils/ConfigurationAdapter';
import HotelsProvider from 'State/HotelsContext';
import { WidgetProvider } from 'State/WidgetContext';


const store = configureStore();
addLocaleData(locales);

const configurationAdapter = new ConfigurationAdapter();

class App {
    static getAllHTMLContainersForWidget(selector, id) {
        if (typeof document === 'undefined' || (typeof document === 'object' && !document)) {
            // we're in a unit test
            return [];
        }

        const containers = document.querySelectorAll(`[${selector}${id ? `=${id}` : ''}]`);

        // This will set an attribute for LESS, so they can be selected and reset for styles purposes
        containers.forEach(container => container.setAttribute('data-kbe-widget', true));
        return containers;
    }

    constructor(window) {
        // The supported methods are used by the executePreloadedParams function, where their meanings are:
        // s: settings, b: search bar, o: rates, p: offers, h: hotel profile, l: hotels list
        this.supportedMethods = [ 'settings', 'searchbar', 'rates', 'offers', 'hotelProfile',
            'hotelslist', 'rewardprogram', 'inquiry' ];
        this.oldSupportedMethods = [ 's', 'b', 'o', 'p', 'h', 'l', 'r', 'e' ];
        this.isIdInitialized = {};
        this.methodsDictionary = {
            settings: 's',
            searchbar: 'b',
            rates: 'o',
            offers: 'p',
            hotelslist: 'l',
            hotels: 'l',
            rewardprogram: 'r',
            inquiry: 'e',
        };

        // all methods that were called till now and stored in queue needs to be called now
        const KBEInstance = window[window['kbe-widgets']];
        const methodsAndParamsQueue = KBEInstance.q;
        this.methodsAndParamsQueue = methodsAndParamsQueue;
        this.handleQueueOfPreloadedParams(methodsAndParamsQueue);

        // The methods for modifying the parameters of each widget are left accessible to the user through a key in the
        // windows variable that is directly accessible, and through which they can assign new properties at runtime
        KBEInstance.searchbar = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'searchbar', runtimeConfiguration });
        KBEInstance.rates = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'rates', runtimeConfiguration });
        KBEInstance.offers = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'offers', runtimeConfiguration });
        KBEInstance.hotelslist = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'hotels', runtimeConfiguration });
        KBEInstance.hotelProfile = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'hotelProfile', runtimeConfiguration });
        KBEInstance.rewardProgram = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'rewardProgram', runtimeConfiguration });
        KBEInstance.inquiry = ({ id, ...runtimeConfiguration }) =>
            this.updateWidget({ id, widgetType: 'inquiry', runtimeConfiguration });

        KBEInstance.settings = this.settings.bind(this);

        this.init = this.init.bind(this);
        this.updateConfigurationOnStore = this.updateConfigurationOnStore.bind(this);
        this.handleQueueOfPreloadedParams = this.handleQueueOfPreloadedParams.bind(this);
        this.executePreloadedParams = this.executePreloadedParams.bind(this);

        this.renderWidgetOnNode = this.renderWidgetOnNode.bind(this);
        this.updateWidget = this.updateWidget.bind(this);
        this.removeWidgetFromNode = this.removeWidgetFromNode.bind(this);
        this.renderAllWidgets = this.renderAllWidgets.bind(this);
        store.dispatch(Search.changeUserAgent(window.navigator.userAgent));
    }

    /**
     * The queue is stored in the client prior to downloading the widget, once downloaded go
     * through the methods pushed by it to be executed.
     */
    async handleQueueOfPreloadedParams(methodsAndParams) {
        if (!methodsAndParams) {
            return;
        }

        let initParams;

        for (const methodsAndParam of methodsAndParams) {
            const [ method, params ] = methodsAndParam;
            const methodLower = method.toLowerCase();

            if (methodLower === 'settings' || methodLower === 's') {
                configurationAdapter.setConfig(params);

                // initialization (which also contains rendering widgets on the page) must NOT happen when the settings
                // parameters are parsed, but after all widget parameters are parsed. This is because some components
                // need to know the widget parameters before they can be rendered.
                initParams = await this.settings(params, false);
            } else {
                this.executePreloadedParams(method, params);
            }
        }

        // init widgets and render components
        if (initParams) {
            await this.init(initParams);
        }
    }

    /**
     * The executePreloadedParams is executed with the methods that were stored in the KBEInstance before the js of the widget
     * finalize its load, once it was downloaded from the server, the requested actions are executed
     */
    executePreloadedParams(type, params) {
        if (!type) {
            console.warn('[KBE-Widget] Widget type required');
        }
        const _type = type.toLowerCase();

        if (_type === 'searchbar' || _type === 'b') {
            configurationAdapter.setKey('isConnected', params);
            this.updateConfigurationOnStore(params.id, 'searchbar', { isConnected: configurationAdapter.config.isConnected });
        }

        const adaptedParams = (_type === 's' || _type === 'settings') ? { ...configurationAdapter.config, ...params } : { ...params };

        const paramsWithButtonConfig = adaptedParams.isConnected ? { ...adaptedParams, searchButtonEnabled: false } : adaptedParams;
        const { id, ...runtimeConfiguration } = paramsWithButtonConfig;

        if (this.oldSupportedMethods.includes(_type) || this.supportedMethods.includes(_type)) {
            // This keeps retro compatibility for both initialization snippets
            const keyInStore = Object.keys(this.methodsDictionary)?.find(key => this.methodsDictionary[key] === _type || key === _type);

            // TODO: this has to be refactored ASAP, as it makes hard adding a new widget!
            const WIDGETS_TYPE = {
                hotelslist    : 'hotels',
                rewardprogram : 'rewardProgram',
                inquiry       : 'inquiry',
            };

            const finalWidgetType = WIDGETS_TYPE[keyInStore] || keyInStore;

            this.updateWidget({
                id,
                widgetType: finalWidgetType,
                runtimeConfiguration,
            });
        } else {
            console.warn(`[KBE-Widget] Incorrect type: ${_type}`);
        }
    }

    /**
     * Saves in Redux store the quantity of loading blocks that the user has established
     */
    setNumberOfLoadingBlocks(id, loadingBlocksForRates = 0, loadingBlocksForOffers = 0) {
        if (loadingBlocksForRates || loadingBlocksForOffers) {
            store.dispatch(WidgetPool.setLoadingBlocks(id, loadingBlocksForRates, loadingBlocksForOffers));
        }
    }

    setNumberOfOffers(id, numberOfOffers = 3) {
        store.dispatch(WidgetPool.setNumberOfOffers(id, numberOfOffers));
    }

    /**
     * This method serves as an initializer of the widget, it is necessary to invoke it to request the initial
     * information, and indicate that an instance of the widget has been created, it also provides the global
     * methods to change languages and currency
     */
    async settings(config, forceInit = true) {
        const { id, channelId, propertyCode, urlTransformers } = config;

        if (!config.language) {
            store.dispatch(Translations.toggleShouldTakeFromApi());
        }

        if (id && !this.isIdInitialized[id]) {
            const adapterConfig = configurationAdapter.getConfiguration();
            const { settings } = await store.dispatch(Settings.getData(config));
            const finalLanguage = settings.language || adapterConfig.language || DEFAULT_FALLBACK_LANGUAGE;
            await store.dispatch(Translations.getTranslations(finalLanguage, channelId, propertyCode));

            if (forceInit) {
                await this.init({ ...config, ...adapterConfig });
            }

            if (!!urlTransformers?.length) {
                UrlTransformers.instance().clear();

                urlTransformers.forEach(urlTransformer => {
                    const sanitizedTransformer = UrlTransformers.getTransformer(urlTransformer);
                    UrlTransformers.instance().addTransformer(sanitizedTransformer);
                });
            }

            return { ...config, ...adapterConfig };
        }

        // These methods are executed at runtime to allow an external application to modify the currency
        // and language of the widgets
        if ('currency' in config) {
            store.dispatch(Search.changeCurrency(config.currency));
        }
        if ('language' in config) {
            const { language } = await store.dispatch(Search.changeLanguage(config.language));
            store.dispatch(Translations.getTranslations(language, channelId, propertyCode));
        }
        if ('currencyDisplay' in config || 'sortOrder' in config) {
            store.dispatch(WidgetPool.setConfig(id, 'config', config));
        }
    }

    async init(config) {
        // If the bar is connected we set the initial state of the search button as disabled
        const { loadingBlocksForRates, loadingBlocksForOffers, numberOfOffers, ...initConfig } = config.isConnected
            ? { ...config, searchButtonEnabled: false }
            : config;
        const { id } = config;

        this.setNumberOfLoadingBlocks(id, loadingBlocksForRates, loadingBlocksForOffers);

        if (typeof numberOfOffers === 'number' && numberOfOffers > 0) {
            this.setNumberOfOffers(id, numberOfOffers);
        }

        const { enabledTrackingModules } = await store.dispatch(Settings.getTrackingModules());
        const { isMultiProperty } = config;

        if (enabledTrackingModules?.length) {
            initTrackingModules(isMultiProperty, enabledTrackingModules);
        }

        try {
            const { customizations = { styles: '', stylesUrl: null } } = await store.dispatch(Settings.getData(initConfig));

            if (!!customizations.styles) {
                appendStylesToHead(customizations.styles);
            } else if (!!customizations.stylesUrl) {
                appendStylesLinkToHead(customizations.stylesUrl);
            }

            const widgetSet = await store.dispatch(WidgetPool.setConfig(id, 'config', initConfig));

            const {
                onWidgetLoaded,
                onViewRooms,
                onPropertiesRendered = () => { },
                onSearchBarClick,
            } = initConfig;

            // Will be passed directly to containers to avoid storing them at redux
            const callBackFunctions = {
                onWidgetLoaded,
                onViewRooms,
                onPropertiesRendered,
                onSearchBarClick,
            };

            this.renderAllWidgets(id, widgetSet, callBackFunctions);

            this.isIdInitialized[id] = true;
        } catch (error) {
            console.warn('[KBE-Widget] There was a problem in the initialization: ', error);
        }
    }

    updateConfigurationOnStore(id, widgetType, configuration) {
        store.dispatch(WidgetPool.setConfig(id, widgetType, configuration));
    }

    renderAllWidgets(id, widgetSet, callBackFunctions) {
        const isWidgetSetEnabled = widgetSet?.config?.isSetDisabled === false;

        if (isWidgetSetEnabled) {
            Object.keys(widgetSet).forEach(widgetType => {
                const isWidgetEnabled = !widgetSet[widgetType].isDisabled;

                if (isWidgetEnabled) {
                    this.updateWidget({
                        id,
                        widgetType,
                        widgetConfiguration  : widgetSet[widgetType],
                        runtimeConfiguration : {},
                        callBackFunctions,
                    });
                }
            });
        }
    }

    /**
     * It receives what kind of widget you want, and can receive configurations like in which container to render,
     * if nothing is indicated the default values will be used, it also has the capacity to delete the indicated widget
     */
    updateWidget({ id, widgetType, widgetConfiguration = null, runtimeConfiguration = {}, callBackFunctions = {} }) {
        // Used jic runtimeConfig is empty and need to be taken from store
        // eslint-disable-next-line no-unused-vars
        let storeRuntimeConfiguration;
        // isRendered and filterByCode can be invoked at runtime,then we get general configurations
        // on currentWidget and the html container where we gonna render this widget
        const { isRendered: letItRendered = true, ...configurationForStore } = widgetConfiguration || runtimeConfiguration;

        // This method is always executed, no matter if you make a new render or not, because
        // it is called to update the configuration values you want to change at runtime

        const takenId = id || runtimeConfiguration?.id || widgetConfiguration?.id;

        this.updateConfigurationOnStore(takenId, widgetType, configurationForStore);

        if (widgetType !== 'config') {
            const currentWidget = widgetConfiguration || store.getState().widgetPool
                ?.find(widgetSet => (widgetSet.config?.id === runtimeConfiguration?.id || !runtimeConfiguration?.id)
                    && !widgetSet.isDisabled && !widgetSet?.config?.isSetDisabled)[widgetType];

            const { channelId, propertyCode } = store.getState().settings.data;

            // The current widget will be rendered in each container that has the necessary attributes
            const htmlContainerNodes = currentWidget?.container && takenId &&
                App.getAllHTMLContainersForWidget(currentWidget.container, takenId);

            if (htmlContainerNodes?.length > 0) {
                if ((!currentWidget.isRendered && !currentWidget.isDisabled)) {
                    htmlContainerNodes.forEach(node => this.renderWidgetOnNode(
                        id,
                        widgetType,
                        node,
                        widgetConfiguration || (storeRuntimeConfiguration?.[widgetType].config) || null,
                        callBackFunctions,
                        channelId,
                        propertyCode,
                    ));
                } else if ((currentWidget.isRendered && !letItRendered) || currentWidget.isDisabled) {
                    // With the letItRendered parameter in false the widget is removed
                    htmlContainerNodes.forEach(node => this.removeWidgetFromNode(takenId, widgetType, node));
                }
            } else {
                // eslint-disable-next-line max-len
                console.warn(`[KBE-Widget] ${widgetType.charAt(0).toUpperCase()}${widgetType.slice(1)} with ID: ${runtimeConfiguration?.id} - Container not found`);
            }
        }
    }

    removeWidgetFromNode(id, widgetType, node) {
        ReactDOM.unmountComponentAtNode(node);
        this.updateConfigurationOnStore(id, widgetType, { isRendered: false });
    }

    async decideWidget(id, widgetType, config, node, callBackFunctions) {
        const { onWidgetLoaded, onPropertiesRendered, onSearchBarClick } = callBackFunctions;

        if (widgetType === 'searchbar') {
            return (
                <SearchBar
                    id={id}
                    container={node}
                    onWidgetLoaded={onWidgetLoaded}
                    onPropertiesRendered={onPropertiesRendered}
                    onSearchBarClick={onSearchBarClick}
                />
            );
        }

        const renderListWidget = widgetType === 'rates' || widgetType === 'offers';
        if (renderListWidget) {
            return (
                <List
                    id={id}
                    container={node}
                    isOffer={widgetType === 'offers'}
                    {...callBackFunctions}
                />
            );
        }

        if (widgetType === 'hotels') {
            return (
                <HotelsProvider id={id} widgetType="hotels">
                    <PropertyListContainer
                        id={id}
                        container={node}
                        {...callBackFunctions}
                    />
                </HotelsProvider>
            );
        }

        if (widgetType === 'rewardProgram') {
            const { search: { data: { language } } } = store.getState();
            return (
                <WidgetProvider id={id} widgetType={widgetType} settingsNeededParams={SETTINGS_NEEDED_PARAMS.rewardProgram}>
                    <RewardProgram
                        layout={config.layout}
                        language={language}
                        dispatch={store.dispatch}
                    />
                </WidgetProvider>
            );
        }

        if (widgetType === 'inquiry') {
            return (
                <EnquiryFormContainer
                    id={id}
                    container={node}
                    {...callBackFunctions}
                />
            );
        }
        // Leave following in order to avoid re rendering for hotels widget
        return <div />;
    }

    async renderWidgetOnNode(id, widgetType, node, config, callBackFunctions, channelId = undefined, propertyCode = undefined) {
        // This is to avoid that a widget node takes the config that doesn't match with it's id
        const widgetKeyMap = {
            rates         : 'kbeRates',
            searchbar     : 'kbeSearchbar',
            offers        : 'kbeOffers',
            hotels        : 'kbeHotelslist',
            hotelProfile  : 'kbeHotelProfile',
            rewardProgram : 'kbeRewardProgram',
            inquiry       : 'kbeInquiry',
        };

        if (id !== node.dataset[widgetKeyMap[widgetType]] || !config) {
            return;
        }

        ReactDOM.render(
            <Provider store={store}>
                {
                    await this.decideWidget(id, widgetType, config, node, callBackFunctions)
                }
            </Provider>,
            node,
        );

        const domainUrl = window ? window.location.hostname : null;

        // Data is coming in an array of arrays, in which position 0 means the key of the widget and position 1 is the widget config
        const findConfig = widgetKey => this.methodsAndParamsQueue
            ?.find(deepConfigArray => deepConfigArray?.find(arrayItem => typeof arrayItem === 'string' && arrayItem === widgetKey));

        const generalConfig = findConfig('s') || findConfig('settings');
        const finalKey = Object.keys(this.methodsDictionary)?.find(key => this.methodsDictionary[key] === widgetType || key === widgetType);
        const specificConfig = findConfig(finalKey === 'hotels' ? 'hotelslist' : finalKey);

        // Users might rename the __KBE id if they mess with the snippet so make sure not to break anything
        const loaderUrl = document.querySelector('#__KBE')?.getAttribute('src');

        const widgetLoaded = {
            integrationDomain: domainUrl,
            widgetType, // ("searchBar" | "roomList" | "offersList")
            loaderUrl,
            ...(propertyCode !== undefined && { propertyId: propertyCode }),
            ...(channelId !== undefined && { channelId }),
        };

        if (generalConfig) {
            widgetLoaded.configuration = { settings: generalConfig[1] };

            if (specificConfig) {
                // eslint-disable-next-line prefer-destructuring
                widgetLoaded.configuration[widgetType] = specificConfig[1];
            }
        }

        trackEvent('widgetLoaded', widgetLoaded);
        this.updateConfigurationOnStore(id || config?.id, widgetType, { isRendered: true });
    }
}

window.KBEWidgetInstance = new App(window);
// This is mandatory for modals to work.
const portalNode = document.createElement('div');
portalNode.id = 'react-portal';
document.querySelector('body').prepend(portalNode);
